import {
  getClient,
  createConsoleLogger,
  LogLevel,
  PollingMode,
  IConfigCatClient,
  IOptions,
  User as ConfigCatUser,
} from 'configcat-js';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Subject,
  switchMap,
} from 'rxjs';
import { environment } from '@environments/environment';
import { FeatureFlagName } from '@common/constant/feature-flags';

interface FeatureFlagClient<U = any> {
  identifyUser(user: U): void;
  getAllFeatures(): Promise<Record<string, boolean>>;
  isFeatureEnabled(key: string): Promise<boolean>;
}

interface ConfigCatClientOptions {
  sdkKey: string;
  logLevel?: LogLevel;
  pollIntervalSeconds?: number;
  setupHooks?: IOptions['setupHooks'];
}

class ConfigCatClient implements FeatureFlagClient<ConfigCatUser> {
  private client: IConfigCatClient;

  private user: ConfigCatUser;

  constructor(private options: ConfigCatClientOptions) {
    this.client = this.createClient();
  }

  identifyUser(user: ConfigCatUser): void {
    this.user = user;
  }

  isFeatureEnabled(key: string): Promise<boolean> {
    return this.client.getValueAsync(key, false, this.user);
  }

  async getAllFeatures() {
    const values = await this.client.getAllValuesAsync(this.user);
    return values.reduce((obj, next) => {
      // eslint-disable-next-line no-param-reassign
      obj[next.settingKey] = next.settingValue as boolean;
      return obj;
    }, {} as Record<string, boolean>);
  }

  private createClient() {
    const {
      sdkKey,
      logLevel = LogLevel.Info,
      pollIntervalSeconds,
      setupHooks,
    } = this.options;

    const logger = createConsoleLogger(logLevel);

    return getClient(sdkKey, PollingMode.AutoPoll, {
      pollIntervalSeconds,
      logger,
      setupHooks,
    });
  }
}

class DefaultClient implements FeatureFlagClient {
  identifyUser(_user: ConfigCatUser): void {}

  getAllFeatures() {
    return Promise.resolve({});
  }

  isFeatureEnabled(_key: string): Promise<boolean> {
    return Promise.resolve(false);
  }
}

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  client: FeatureFlagClient;

  private clientConfigChanged$ = new Subject<void>();

  private user$ = new BehaviorSubject<ConfigCatUser>(null);

  private featureFlagValues$ = new BehaviorSubject<
    Record<FeatureFlagName, boolean>
  >({} as Record<FeatureFlagName, boolean>);

  constructor() {
    if (environment.config_cat_sdk) {
      this.client = new ConfigCatClient({
        sdkKey: environment.config_cat_sdk,
        logLevel: environment.debug ? LogLevel.Warn : LogLevel.Error,
        setupHooks: (hooks) => {
          hooks.on('configChanged', () => this.clientConfigChanged$.next());
        },
      });
    } else {
      this.client = new DefaultClient();
    }

    combineLatest([this.clientConfigChanged$, this.user$])
      .pipe(
        filter(([, user]) => !!user),
        switchMap(() => this.client.getAllFeatures())
      )
      .subscribe(this.featureFlagValues$);
  }

  identifyUser(user: ConfigCatUser) {
    this.client.identifyUser(user);
    this.user$.next(user);
  }

  getAllFeatures() {
    return this.featureFlagValues$.asObservable();
  }

  isFeatureEnabled(flagName: FeatureFlagName) {
    return this.featureFlagValues$.pipe(
      map((features) => features?.[flagName])
    );
  }
}
